import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, inr } from "@/lib/api";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";

const CATS = [
  {id:"e-commerce", label:"E-commerce", desc:"Shop, checkout, catalog."},
  {id:"saas", label:"SaaS", desc:"Dashboards, billing, teams."},
  {id:"portfolio", label:"Portfolio", desc:"Personal or studio."},
  {id:"corporate", label:"Corporate", desc:"Firms, agencies, investors."},
  {id:"blogs", label:"Blog / Media", desc:"Editorial, CMS, subscriptions."},
  {id:"landing", label:"Landing page", desc:"High-conversion single page."},
  {id:"custom", label:"Custom", desc:"Something else entirely."},
];
const STYLES = [
  {id:"minimalist", label:"Minimalist"},
  {id:"bold", label:"Bold"},
  {id:"dark", label:"Dark"},
  {id:"glassmorphism", label:"Glassmorphism"},
  {id:"neumorphism", label:"Neumorphism"},
  {id:"vibrant", label:"Vibrant"},
];
const PACKAGES = [
  {id:"basic", name:"Basic", price:34999, feats:["Up to 5 pages","Design + build","1 revision round","30-day support"]},
  {id:"pro", name:"Pro", price:74999, feats:["Up to 12 pages","Design system + build","3 revision rounds","CMS + basic integrations","60-day support"], recommended:true},
  {id:"enterprise", name:"Enterprise", price:149999, feats:["Complex page systems","Custom dashboards","Advanced integrations","Priority SLA","6-month support"]},
];

export default function OrderFlow() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [state, setState] = useState({
    category:"", style:"", business_name:"", industry:"", audience:"", goals:"",
    pages:"", features:"", references:"", colors:"", package:"", price:0,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const next = () => setStep(s => Math.min(7, s + 1));
  const back = () => setStep(s => Math.max(1, s - 1));

  const canNext = () => {
    if (step===1) return !!state.category;
    if (step===2) return !!state.style;
    if (step===3) return state.business_name && state.industry && state.audience && state.goals;
    if (step===4) return state.pages && state.features;
    if (step===5) return !!state.package;
    return true;
  };

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders", state);
      setDone(data);
      setStep(7);
      toast.success("Project brief received. No payment was captured.");
    } catch (e) {
      toast.error(errMsg(e, "Order failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-16" data-testid="order-flow">
      <div className="label-mono mb-3">06 · Order flow</div>
      <h1 className="font-display font-black text-4xl md:text-5xl">Craft your build brief.</h1>

      {/* stepper */}
      <div className="mt-10 flex flex-wrap gap-2">
        {[1,2,3,4,5,6,7].map(n => (
          <div key={n} data-testid={`step-indicator-${n}`}
            className={`px-3 py-1 rounded-full text-xs font-mono ${step>=n ? "bg-violet-500/20 text-violet-200 border border-violet-400/30" : "border border-white/10 text-white/40"}`}>
            {n <= 6 ? `Step ${n}` : "Done"}
          </div>
        ))}
      </div>

      <div className="mt-10 glass rounded-2xl p-8 min-h-[380px]">
        {step===1 && (
          <div data-testid="step-1">
            <div className="label-mono mb-4">Choose category</div>
            <div className="grid md:grid-cols-2 gap-4">
              {CATS.map(c => (
                <button key={c.id} onClick={()=>setState({...state,category:c.id})}
                  data-testid={`cat-${c.id}`}
                  className={`text-left p-5 rounded-xl border transition-colors ${state.category===c.id ? "border-violet-400 bg-violet-500/10" : "border-white/10 hover:border-white/30"}`}>
                  <div className="font-display font-bold text-xl">{c.label}</div>
                  <div className="text-white/60 text-sm mt-1">{c.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {step===2 && (
          <div data-testid="step-2">
            <div className="label-mono mb-4">Pick a visual style</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {STYLES.map(s => (
                <button key={s.id} onClick={()=>setState({...state,style:s.id})}
                  data-testid={`style-${s.id}`}
                  className={`aspect-[4/3] rounded-xl border overflow-hidden flex items-end p-4 transition-colors ${state.style===s.id ? "border-violet-400" : "border-white/10 hover:border-white/30"}`}
                  style={{background: s.id==="dark"?"linear-gradient(135deg,#111,#000)":s.id==="vibrant"?"linear-gradient(135deg,#8B5CF6,#06B6D4)":s.id==="glassmorphism"?"linear-gradient(135deg,rgba(139,92,246,0.3),rgba(6,182,212,0.2))":s.id==="bold"?"linear-gradient(135deg,#F59E0B,#EF4444)":s.id==="neumorphism"?"linear-gradient(135deg,#1a1a24,#0f0f14)":"linear-gradient(135deg,#0A0A10,#12121A)"}}>
                  <span className="font-display font-bold">{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {step===3 && (
          <div data-testid="step-3" className="grid md:grid-cols-2 gap-4">
            <input placeholder="Business name" value={state.business_name} onChange={e=>setState({...state,business_name:e.target.value})} data-testid="biz-name" className="bg-transparent hairline-strong rounded-lg px-4 py-3 outline-none focus:border-violet-500"/>
            <input placeholder="Industry" value={state.industry} onChange={e=>setState({...state,industry:e.target.value})} data-testid="biz-industry" className="bg-transparent hairline-strong rounded-lg px-4 py-3 outline-none focus:border-violet-500"/>
            <input placeholder="Target audience" value={state.audience} onChange={e=>setState({...state,audience:e.target.value})} data-testid="biz-audience" className="bg-transparent hairline-strong rounded-lg px-4 py-3 outline-none focus:border-violet-500 md:col-span-2"/>
            <textarea placeholder="Business goals" rows={4} value={state.goals} onChange={e=>setState({...state,goals:e.target.value})} data-testid="biz-goals" className="bg-transparent hairline-strong rounded-lg px-4 py-3 outline-none focus:border-violet-500 md:col-span-2"/>
          </div>
        )}
        {step===4 && (
          <div data-testid="step-4" className="grid md:grid-cols-2 gap-4">
            <textarea placeholder="Pages you need (Home, About, Pricing…)" rows={3} value={state.pages} onChange={e=>setState({...state,pages:e.target.value})} data-testid="site-pages" className="bg-transparent hairline-strong rounded-lg px-4 py-3 outline-none focus:border-violet-500"/>
            <textarea placeholder="Features (auth, payments, chat…)" rows={3} value={state.features} onChange={e=>setState({...state,features:e.target.value})} data-testid="site-features" className="bg-transparent hairline-strong rounded-lg px-4 py-3 outline-none focus:border-violet-500"/>
            <input placeholder="Reference / inspiration links" value={state.references} onChange={e=>setState({...state,references:e.target.value})} data-testid="site-refs" className="bg-transparent hairline-strong rounded-lg px-4 py-3 outline-none focus:border-violet-500 md:col-span-2"/>
            <input placeholder="Color preferences" value={state.colors} onChange={e=>setState({...state,colors:e.target.value})} data-testid="site-colors" className="bg-transparent hairline-strong rounded-lg px-4 py-3 outline-none focus:border-violet-500 md:col-span-2"/>
          </div>
        )}
        {step===5 && (
          <div data-testid="step-5">
            <div className="label-mono mb-4">Select package</div>
            <div className="grid md:grid-cols-3 gap-4">
              {PACKAGES.map(p => (
                <button key={p.id} onClick={()=>setState({...state, package:p.id, price:p.price})}
                  data-testid={`pkg-${p.id}`}
                  className={`relative text-left p-6 rounded-xl border transition-colors ${state.package===p.id ? "border-violet-400 bg-violet-500/10" : "border-white/10 hover:border-white/30"}`}>
                  {p.recommended && <span className="absolute -top-2 right-4 chip cyan">Recommended</span>}
                  <div className="font-display font-bold text-2xl">{p.name}</div>
                  <div className="text-3xl font-display font-black mt-2">{inr(p.price)}</div>
                  <ul className="mt-4 space-y-1.5 text-sm text-white/70">
                    {p.feats.map(f => <li key={f} className="flex items-center gap-2"><Check size={14} className="text-emerald-400"/>{f}</li>)}
                  </ul>
                </button>
              ))}
            </div>
          </div>
        )}
        {step===6 && (
          <div data-testid="step-6" className="text-center">
            <div className="label-mono">Review your brief</div>
            <h3 className="font-display font-black text-3xl mt-2">{PACKAGES.find(p=>p.id===state.package)?.name} — from {inr(state.price)}</h3>
            <p className="text-white/60 mt-3 max-w-md mx-auto text-sm">Submitting creates your private project workspace. This is an estimate only; no payment will be captured until scope and commercial terms are agreed.</p>
            <button onClick={pay} disabled={busy} className="pill-btn violet mt-8" data-testid="pay-btn">
              {busy ? "Submitting…" : "Submit project brief"}
            </button>
          </div>
        )}
        {step===7 && done && (
          <div data-testid="step-7" className="text-center">
            <Check size={44} className="mx-auto text-emerald-400"/>
            <h3 className="font-display font-black text-3xl mt-3">Brief received</h3>
            <p className="text-white/60 mt-2">Reference <span className="font-mono">#{done.order_id}</span> · Project <span className="font-mono">#{done.project_id}</span></p>
            <button onClick={()=>nav("/dashboard")} className="pill-btn primary mt-8" data-testid="goto-dashboard">Go to dashboard</button>
          </div>
        )}
      </div>

      {step < 6 && (
        <div className="mt-6 flex items-center justify-between">
          <button onClick={back} disabled={step===1} className="pill-btn ghost disabled:opacity-40" data-testid="order-back">
            <ArrowLeft size={14}/>Back
          </button>
          <button onClick={next} disabled={!canNext()} className="pill-btn primary disabled:opacity-40" data-testid="order-next">
            Next <ArrowRight size={14}/>
          </button>
        </div>
      )}
    </div>
  );
}
